import React, { Suspense, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ShaderGradientCanvas, ShaderGradient } from '@shadergradient/react';
import { getCtaShaderProps } from '../effects/shaderGradientPresets.js';
import { useProducts, getProductIcon } from '../../../hooks/useProducts.jsx';
import { useIsCompactLayout } from '../../../hooks/useIsCompactLayout.js';
import { useInfiniteCarouselScroll } from '../../../hooks/useInfiniteCarouselScroll.js';
import { useCarouselActiveIndex } from '../../../hooks/useCarouselActiveIndex.js';
import CarouselIndicator from '../CarouselIndicator.jsx';
import LoadingSpinner from '../../common/LoadingSpinner.jsx';
import { usePanelRevealObserver } from '../../../hooks/usePanelRevealObserver.js';

/**
 * 製品紹介ページ
 */
const ProductsPage = () => {
  const isMobile = useIsCompactLayout();
  const [activeSection, setActiveSection] = useState('content');
  const contentRef = useRef(null);
  const ctaRef = useRef(null);
  const carouselRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-40% 0px -40% 0px',
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.dataset.section);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    if (contentRef.current) observer.observe(contentRef.current);
    if (ctaRef.current) observer.observe(ctaRef.current);

    return () => observer.disconnect();
  }, []);

  // Add body class for products page styling
  useEffect(() => {
    document.body.classList.add('hub-products-page');
    return () => {
      document.body.classList.remove('hub-products-page');
    };
  }, []);

  // CMSから製品データを取得
  const { products, loading: productsLoading, error: productsError } = useProducts();

  useInfiniteCarouselScroll(carouselRef, products.length, isMobile && products.length > 0);
  const carouselActiveIndex = useCarouselActiveIndex(carouselRef, products.length, isMobile && products.length > 0);

  usePanelRevealObserver('.hub-products-page .hub-panel-animated', [products.length], '400px 0px -5% 0px');

  return (
    <>
      {/* Fixed Gradient Backgrounds */}
      <div className="hub-fixed-gradient-container">
        {/* BG03 - Modules (Content Section) */}
        <div className={`hub-fixed-gradient-layer ${activeSection === 'content' ? 'active' : ''}`}>
          <ShaderGradientCanvas
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            <Suspense fallback={null}>
              <ShaderGradient
                animate="on"
                axesHelper="off"
                bgColor1="#000000"
                bgColor2="#000000"
                brightness={1.2}
                cAzimuthAngle={180}
                cDistance={2.5}
                cPolarAngle={120}
                cameraZoom={1}
                color1="#ebedff"
                color2="#f3f2f8"
                color3="#dbf8ff"
                destination="onCanvas"
                embedMode="off"
                envPreset="city"
                format="gif"
                fov={45}
                frameRate={10}
                gizmoHelper="hide"
                grain="off"
                lightType="3d"
                pixelDensity={1}
                positionX={0}
                positionY={1.8}
                positionZ={0}
                range="disabled"
                rangeEnd={40}
                rangeStart={0}
                reflection={0.1}
                rotationX={0}
                rotationY={0}
                rotationZ={-90}
                shader="defaults"
                type="waterPlane"
                uAmplitude={0}
                uDensity={1.3}
                uFrequency={5.5}
                uSpeed={0.1}
                uStrength={3}
                uTime={0.2}
                wireframe={false}
                zoomOut={false}
              />
            </Suspense>
          </ShaderGradientCanvas>
        </div>

        {/* BG01 - CTA / BG01_mobil on mobile */}
        <div className={`hub-fixed-gradient-layer ${activeSection === 'cta' ? 'active' : ''}`}>
          <ShaderGradientCanvas
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            <Suspense fallback={null}>
              <ShaderGradient {...getCtaShaderProps(isMobile)} />
            </Suspense>
          </ShaderGradientCanvas>
        </div>
      </div>

      {/* ヒーロー */}
      <section 
        className="hub-hero-no-bg"
        style={{ 
          paddingTop: 'var(--hub-space-5xl)',
          paddingBottom: 'var(--hub-space-lg)',
          paddingLeft: 'var(--hub-space-xl)',
          paddingRight: 'var(--hub-space-xl)',
          textAlign: 'center',
          position: 'relative',
          zIndex: 2
        }}
      >
        <div style={{ maxWidth: 'var(--hub-max-width)', margin: '0 auto' }}>
          <div className="hub-section-badge hub-hero-badge" style={{ background: 'rgba(99, 102, 241, 0.15)', color: 'var(--hub-accent-primary)' }}>
            Products
          </div>
          <h1 className="hub-hero-title" style={{ 
            fontFamily: 'var(--hub-font-display)', 
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: 800,
            marginBottom: 'var(--hub-space-md)',
            color: '#000',
            textShadow: '0 1px 2px rgba(128, 128, 128, 0.3)'
          }}>
            歯科医療に必要なすべてを、<br />ひとつのプラットフォームで
          </h1>
          <p className="hub-hero-subtitle" style={{ 
            fontSize: '1.125rem', 
            color: 'var(--hub-text-secondary)',
            maxWidth: '600px',
            margin: '0 auto',
            textShadow: '0 1px 2px rgba(128, 128, 128, 0.2)'
          }}>
            診断支援から経営分析まで、クリニックの規模やニーズに合わせて
            最適なモジュールを組み合わせてご利用いただけます。
          </p>
        </div>
      </section>

      {/* モジュール一覧 */}
      <section 
        ref={contentRef}
        data-section="content"
        className="hub-section-dark"
        style={{ 
          padding: 'var(--hub-space-4xl) var(--hub-space-xl)',
          position: 'relative',
          zIndex: 1,
          background: 'transparent'
        }}
      >
        <div style={{ maxWidth: 'var(--hub-max-width)', margin: '0 auto' }}>
          {productsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--hub-space-3xl)' }}>
              <LoadingSpinner size="large" />
            </div>
          ) : productsError ? (
            <div style={{ textAlign: 'center', padding: 'var(--hub-space-3xl)', color: 'var(--hub-text-secondary)' }}>
              製品情報の読み込みに失敗しました
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hub-space-3xl)' }}>
              {isMobile && products.length > 0 ? (
                <>
                <div ref={carouselRef} className="hub-mobile-panel-carousel" role="region" aria-label="製品一覧">
                  {[
                    ...(products.length > 0 ? [products[products.length - 1]] : []),
                    ...products,
                    ...(products.length > 0 ? [products[0]] : []),
                  ].map((product, index) => {
                    const isClone = index === 0 || index === products.length + 1;
                    const key = isClone ? `carousel-clone-${index === 0 ? 'last' : 'first'}-${product.id}` : product.id;
                    return (
                      <div key={key} className="hub-mobile-panel-carousel-slide">
                        <div className="hub-module-card hub-panel-animated visible">
                          <div className="hub-module-icon" style={{ marginBottom: 'var(--hub-space-lg)' }}>
                            {getProductIcon(product.iconType)}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--hub-space-sm)', marginBottom: 'var(--hub-space-xs)' }}>
                            <h2 style={{ fontFamily: 'var(--hub-font-display)', fontSize: '1.25rem', fontWeight: 700 }}>
                              {product.name}
                            </h2>
                            {product.isComingSoon && <span className="hub-badge hub-badge-warning">準備中</span>}
                          </div>
                          <p style={{ fontSize: '1rem', color: 'var(--hub-accent-primary)', fontWeight: 600, marginBottom: 'var(--hub-space-md)' }}>
                            {product.shortDescription}
                          </p>
                          <p style={{ color: 'var(--hub-text-secondary)', lineHeight: 1.7, marginBottom: 'var(--hub-space-lg)', flex: 1 }}>
                            {product.description}
                          </p>
                          <h4 style={{ fontWeight: 600, marginBottom: 'var(--hub-space-md)', color: 'var(--hub-text-primary)' }}>主な機能</h4>
                          <ul className="hub-module-features" style={{ marginBottom: 'var(--hub-space-md)' }}>
                            {(product.features || []).slice(0, 4).map((feature, i) => (
                              <li key={i}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                                {feature}
                              </li>
                            ))}
                          </ul>
                          {!product.isComingSoon ? (
                            <Link to="/signup" className="hub-btn hub-btn-gradient">無料で試す</Link>
                          ) : (
                            <button className="hub-btn hub-btn-secondary" disabled>Coming Soon</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <CarouselIndicator count={products.length} activeIndex={carouselActiveIndex} />
                </>
              ) : (
                products.map((product, index) => (
                <div 
                  key={product.id}
                  className="hub-module-card hub-panel-animated"
                  style={{ 
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 'var(--hub-space-2xl)',
                    alignItems: 'center',
                    animationDelay: `${index * 0.1}s`,
                    width: '85%',
                    margin: '0 auto',
                    paddingLeft: '60px'
                  }}
                >
                  <div style={{ order: 1 }}>
                    <div className="hub-module-icon" style={{ marginBottom: 'var(--hub-space-lg)' }}>
                      {getProductIcon(product.iconType)}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--hub-space-sm)', marginBottom: 'var(--hub-space-xs)' }}>
                      <h2 style={{ 
                        fontFamily: 'var(--hub-font-display)',
                        fontSize: '1.75rem',
                        fontWeight: 700
                      }}>
                        {product.name}
                      </h2>
                      {product.isComingSoon && (
                        <span className="hub-badge hub-badge-warning">準備中</span>
                      )}
                    </div>
                    <p style={{ 
                      fontSize: '1rem',
                      color: 'var(--hub-accent-primary)',
                      fontWeight: 600,
                      marginBottom: 'var(--hub-space-md)'
                    }}>
                      {product.shortDescription}
                    </p>
                    <p style={{ 
                      color: 'var(--hub-text-secondary)',
                      lineHeight: 1.7,
                      marginBottom: 'var(--hub-space-lg)'
                    }}>
                      {product.description}
                    </p>
                    {!product.isComingSoon ? (
                      <Link to="/signup" className="hub-btn hub-btn-gradient">
                        無料で試す
                      </Link>
                    ) : (
                      <button className="hub-btn hub-btn-secondary" disabled>
                        Coming Soon
                      </button>
                    )}
                  </div>
                  
                  <div style={{ order: 2 }}>
                    <div>
                      <h4 style={{ 
                        fontWeight: 600,
                        marginBottom: 'var(--hub-space-md)',
                        color: 'var(--hub-text-primary)'
                      }}>
                        主な機能
                      </h4>
                      <ul className="hub-module-features" style={{ marginBottom: 'var(--hub-space-xl)' }}>
                        {(product.features || []).map((feature, i) => (
                          <li key={i}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                            {feature}
                          </li>
                        ))}
                      </ul>
                      
                      {(product.useCases || []).length > 0 && (
                        <>
                          <h4 style={{ 
                            fontWeight: 600,
                            marginBottom: 'var(--hub-space-md)',
                            color: 'var(--hub-text-primary)'
                          }}>
                            活用シーン
                          </h4>
                          <ul style={{ 
                            listStyle: 'none',
                            padding: 0,
                            margin: 0,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 'var(--hub-space-sm)'
                          }}>
                            {product.useCases.map((useCase, i) => (
                              <li 
                                key={i}
                                style={{
                                  background: 'var(--hub-accent-primary-light)',
                                  color: 'var(--hub-accent-primary)',
                                  padding: '0.375rem 0.75rem',
                                  borderRadius: 'var(--hub-radius-full)',
                                  fontSize: '0.8125rem',
                                  fontWeight: 500
                                }}
                              >
                                {useCase}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section 
        ref={ctaRef}
        data-section="cta"
        className="hub-cta"
        style={{ position: 'relative', zIndex: 1 }}
      >
        <div className="hub-cta-inner">
          <h2 className="hub-cta-title">
            まずは無料で始めましょう
          </h2>
          <p className="hub-cta-subtitle">
            14日間すべての機能をご利用いただけます。
            お気軽にお試しください。
          </p>
          <div className="hub-cta-actions">
            <Link to="/signup" className="hub-btn hub-btn-gradient hub-btn-lg">
              無料トライアルを開始
            </Link>
            <Link to="/pricing" className="hub-btn hub-btn-glass-dark hub-btn-lg">
              料金プランを見る
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ProductsPage;

